import { Typography, Grid, Button, Card } from '@material-ui/core'
import Dialog from '@material-ui/core/Dialog'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import React, { useState } from 'react'
import { FeatureBlocksProposalFragment } from './FeatureBlocks.generated-types'
import { SpaceDocumentTag } from '../../../types'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemText from '@material-ui/core/ListItemText'
import { DialogTitle } from '../../../Components/UI/Dialog/DialogTitle'
import { PaperClipOutlined, DownloadOutlined } from '../../../Components/Icons/Icons'
import { CapacityIcon, ThreeDIcon, FloorplansIcon, SpecificationsIcon } from './icons'
import './styles.less'
import { s } from './style'

const spatialConfigurationLabels: Record<string, string> = {
  boardroom: 'Boardroom',
  cabaret: 'Cabaret',
  classroom: 'Classroom',
  seating: 'Seating',
  standing: 'Standing',
  theatre: 'Theater style',
  'u-shaped': 'U-shaped',
  dinner_rounds: 'Dinner / Rounds',
}

interface FeatureBlockProps {
  proposal: FeatureBlocksProposalFragment
}

export const FeatureBlock: React.FC<FeatureBlockProps> = ({
  proposal,
}) => {
  const {
    space: {
      spatialConfigurations,
      spaceFeatures,
      tour3dUrl,
      spaceDocuments,
    },
  } = proposal

  const floorplan = spaceDocuments.filter(doc => doc.tag === SpaceDocumentTag.Floorplan)
  const specifications = spaceDocuments.filter(doc => doc.tag === SpaceDocumentTag.Specifications)
  const [isCapacityModalOpen, setIsCapacityModalOpen] = useState(false)
  const [isFloorplanModalOpen, setIsFloorplanModalOpen] = useState(false)
  const [isSpecificationsModalOpen, setIsSpecificationsModalOpen] = useState(false)

  const showSpecificationsModal = (): void => {
    setIsSpecificationsModalOpen(true)
  }

  const showFloorplanModal = (): void => {
    setIsFloorplanModalOpen(true)
  }

  const showCapacityModal = (): void => {
    setIsCapacityModalOpen(true)
  }

  const handleCancel = (): void => {
    setIsCapacityModalOpen(false)
    setIsFloorplanModalOpen(false)
    setIsSpecificationsModalOpen(false)
  }

  const maxCapacity = (): number => {
    return Math.max(...spatialConfigurations.map(s => s.capacity))
  }

  const facilities = []
  if (spaceFeatures?.length > 0) {
    spaceFeatures.forEach((f: any) => facilities.push(f.name))
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={3}>
        <Card className={`block ${spatialConfigurations ? '' : 'unableBlock'}`}>
          <Typography className='blockTitle' variant="subtitle1" gutterBottom>
            <CapacityIcon className='blockIcon' viewBox="0 0 16 16"/>
            {`Max capacity: ${maxCapacity()}`}
          </Typography>
          <Button className='blockLink' size="small" onClick={showCapacityModal} disabled={!spatialConfigurations}>
            See possible layouts
          </Button>
        </Card>
        <Dialog className='dialog' open={isCapacityModalOpen} onClose={handleCancel}>
          <DialogTitle id="dialogTitleLayout" onClose={handleCancel}>Room layouts</DialogTitle>
          <DialogContent>
            <DialogContentText>
              <Grid container spacing={2}>
                {
                  spatialConfigurations.map((configuration) => (
                    <Grid item xs={6} key={configuration.name}>
                      <Typography variant="subtitle2" style={s.layoutName} gutterBottom>
                        {spatialConfigurationLabels[configuration.name]}: {configuration.capacity}
                      </Typography>
                    </Grid>
                  ))
                }
              </Grid>
            </DialogContentText>
          </DialogContent>
        </Dialog>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card className={`block ${tour3dUrl ? '' : 'unableBlock'}`}>
          <h4 className='blockTitle'>
            <ThreeDIcon className='blockIcon' viewBox="0 0 16 16"/>
            3D tour
          </h4>
          <Button className="blockLink" type="link" target='_blank' href={tour3dUrl} rel='noreferrer' disabled={!tour3dUrl}>
            A full virtual tour of the space
          </Button>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card className={`block ${floorplan.length ? '' : 'unableBlock'}`}>
          <h4 className='blockTitle'>
            <FloorplansIcon className='blockIcon' viewBox="0 0 16 16"/>
            Floorplan
          </h4>
          <Button
            className="blockLink"
            type="link"
            target='_blank'
            href={ floorplan.length === 1 ? floorplan[0].url : null}
            onClick={ floorplan.length === 1 ? null : showFloorplanModal}
            rel='noreferrer'
            disabled={!floorplan.length}
          >
            Download
          </Button>
        </Card>
        <Dialog className='dialog' open={isFloorplanModalOpen} onClose={handleCancel}>
          <DialogTitle id="dialogTitleFloorplan" onClose={handleCancel}>Choose a floor plan to open</DialogTitle>
          <DialogContent>
            <DialogContentText>
              <List component="nav" className="fileList">
                {
                  floorplan.map((plan) => (
                    <a href={plan.url} target='_blank' rel="noreferrer" key={plan.filename}>
                      <ListItem button className='fileListItem'>
                        <ListItemIcon className='fileListIcon'>
                          <PaperClipOutlined viewBox="0 0 16 16"/>
                        </ListItemIcon>
                        <ListItemText className='fileListName' primary={`${plan.filename}`} />
                        <DownloadOutlined className='fileListDownloadIcon' viewBox="0 0 16 16"/>
                      </ListItem>
                    </a>
                  ))
                }
              </List>
            </DialogContentText>
          </DialogContent>
        </Dialog>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card className={`block ${specifications.length ? '' : 'unableBlock'}`}>
          <h4 className='blockTitle'>
            <SpecificationsIcon className='blockIcon' viewBox="0 0 16 16"/>
            Specifications
          </h4>
          <Button
            className="blockLink"
            type="link"
            target='_blank'
            href={ specifications.length === 1 ? specifications[0].url : null }
            onClick={ specifications.length === 1 ? null : showSpecificationsModal }
            rel='noreferrer'
            disabled={!specifications.length}
          >
            Download
          </Button>
        </Card>
        <Dialog className='dialog' open={isSpecificationsModalOpen} onClose={handleCancel}>
          <DialogTitle id="dialogTitleSpecification" onClose={handleCancel}>Choose specifications to open</DialogTitle>
          <DialogContent>
            <DialogContentText>
              <List component="nav" className="fileList">
                {
                  floorplan.map((specification) => (
                    <a href={specification.url} target='_blank' rel="noreferrer" key={specification.filename}>
                      <ListItem button className='fileListItem'>
                        <ListItemIcon className='fileListIcon'>
                          <PaperClipOutlined viewBox="0 0 16 16"/>
                        </ListItemIcon>
                        <ListItemText className='fileListName' primary={`${specification.filename}`} />
                        <DownloadOutlined className='fileListDownloadIcon' viewBox="0 0 16 16"/>
                      </ListItem>
                    </a>
                  ))
                }
              </List>
            </DialogContentText>
          </DialogContent>
        </Dialog>
      </Grid>
    </Grid>
  )
}
